import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import type { SchedulerFilters } from '../../../types/scheduling';
import { schedulingReturnsService, type SchedulingReturn } from '../../../services/schedulingReturns.service';

type Mode = 'scheduler' | 'lc';
const inputClass = 'rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 disabled:opacity-60';
const buttonClass = 'rounded border border-blue-300 px-2 py-1 text-xs text-blue-700 dark:text-blue-300 disabled:opacity-50';
function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error) && typeof error.response?.data?.detail === 'string') return error.response.data.detail;
  return error instanceof Error ? error.message : 'Could not update the return plan.';
}

export function ReturnPlanEditor({ row, mode, onChanged }: {
  row: SchedulingReturn; mode: Mode; onChanged: () => void;
}) {
  const [date, setDate] = useState(row.proposed_date || '');
  const [start, setStart] = useState(row.time_start || '');
  const [end, setEnd] = useState(row.time_end || '');
  const [comments, setComments] = useState(row.comments || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const editable = mode === 'scheduler' && ['PLANNING', 'RETURNED'].includes(row.status);
  const pendingLC = mode === 'lc' && row.status === 'PENDING';
  const label = `${row.pronumber} leg ${row.leg_num}`;

  async function act(action: 'save' | 'send' | 'finalize' | 'return') {
    setBusy(true);
    setError('');
    try {
      if (action === 'save' || action === 'send') {
        await schedulingReturnsService.update(row.leg_recno, {
          proposed_date: date || null, time_start: start || null,
          time_end: end || null, comments: comments || null,
        });
        if (action === 'send') await schedulingReturnsService.sendToLC(row.leg_recno);
      } else if (action === 'finalize') {
        await schedulingReturnsService.finalize(row.leg_recno);
      } else {
        await schedulingReturnsService.returnToScheduler(row.leg_recno, comments.trim());
      }
      window.dispatchEvent(new Event('scheduling:returns-updated'));
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return <div className="min-w-[390px] space-y-1 py-1">
    <div className="flex flex-wrap items-center gap-1">
      <input type="date" aria-label={`Return date for ${label}`} className={inputClass} value={date}
        disabled={!editable || busy} onChange={e => setDate(e.target.value)} />
      <input type="time" aria-label={`Return start time for ${label}`} className={inputClass} value={start}
        disabled={!editable || busy} onChange={e => setStart(e.target.value)} />
      <span>–</span>
      <input type="time" aria-label={`Return end time for ${label}`} className={inputClass} value={end}
        disabled={!editable || busy} onChange={e => setEnd(e.target.value)} />
    </div>
    <div className="flex flex-wrap items-center gap-1">
      <input aria-label={`Return comments for ${label}`} placeholder="Return comments" maxLength={5000}
        className={`${inputClass} flex-1`} value={comments} disabled={(!editable && !pendingLC) || busy}
        onChange={e => setComments(e.target.value)} />
      {editable && <>
        <button type="button" className={buttonClass} disabled={busy} onClick={() => void act('save')}>Save</button>
        <button type="button" className={buttonClass} disabled={busy || !date} onClick={() => void act('send')}>Send to LC</button>
      </>}
      {pendingLC && <>
        <button type="button" className={buttonClass} disabled={busy} onClick={() => void act('finalize')}>Finalize return</button>
        <button type="button" className={buttonClass} disabled={busy || !comments.trim()} onClick={() => void act('return')}>Return to Scheduler</button>
      </>}
    </div>
    {error && <p role="alert" className="whitespace-normal text-xs text-red-600 dark:text-red-400">{error}</p>}
  </div>;
}

export default function BobtailReturnsPanel({ mode, filters, onOpenOrder }: {
  mode: Mode; filters: SchedulerFilters; onOpenOrder: (pronumber: number) => void;
}) {
  const [rows, setRows] = useState<SchedulingReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  // Only common order filters apply here; delivery/pre-pull filters belong to the delivery queue.
  const query = JSON.stringify({ mode, move_type: filters.move_type,
    terminal: filters.terminal?.join(','), customer: filters.customer?.join(','),
    logistic_coordinator: filters.logistic_coordinator?.join(','),
    pronumber: filters.pronumber, delivery_type: filters.delivery_type });
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    schedulingReturnsService.list(JSON.parse(query)).then(data => {
      if (active) setRows(data);
    }).catch(err => {
      if (active) { setError(errorMessage(err)); setRows([]); }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query, revision]);

  const columns = useMemo<ColumnDef<SchedulingReturn>[]>(() => [
    { accessorKey: 'pronumber', header: 'PRO', cell: ({ row }) =>
      <button className="text-blue-600 dark:text-blue-400 hover:underline" onClick={() => onOpenOrder(row.original.pronumber)}>{row.original.pronumber}</button> },
    { accessorKey: 'leg_num', header: 'Terminate leg' },
    { accessorKey: 'container', header: 'Container' },
    { accessorKey: 'customer', header: 'Customer' },
    { accessorKey: 'terminal', header: 'Terminal' },
    { accessorKey: 'delivery_type', header: 'Local / OTR' },
    { accessorKey: 'pickup', header: 'Return from' },
    { accessorKey: 'return_to', header: 'Return to' },
    { accessorKey: 'status', header: 'Return status', cell: ({ row }) =>
      ({ PLANNING: 'Planning', PENDING: 'Pending LC', RETURNED: 'Returned', FINALIZED: 'Finalized' }[row.original.status]) },
    { id: 'return_plan', header: 'Return appointment', cell: ({ row }) =>
      <ReturnPlanEditor key={JSON.stringify([row.original.leg_recno, row.original.status, row.original.proposed_date, row.original.time_start, row.original.time_end, row.original.comments])}
        row={row.original} mode={mode} onChanged={refresh} /> },
  ], [mode, onOpenOrder, refresh]);

  return <section aria-label="Bobtail returns" className={mode === 'scheduler' ? 'flex h-full min-h-0 flex-col' : 'mb-4'}>
    <div className={`flex shrink-0 items-center justify-between gap-4 ${mode === 'scheduler' ? 'order-last mt-2' : 'mb-2'}`}>
      <p className="text-xs text-gray-600 dark:text-gray-400">Bobtail Approved returns • Uses terminal, move type, Local/OTR, customer, LC and PRO filters. Finalized returns count toward capacity.</p>
      <button type="button" className={buttonClass} disabled={loading} onClick={refresh}>Refresh returns</button>
    </div>
    {error && <p role="alert" className="mb-2 shrink-0 text-sm text-red-600 dark:text-red-400">{error}</p>}
    <div className={mode === 'scheduler' ? 'min-h-0 flex-1' : undefined}>
      <TanTable fillContainer={mode === 'scheduler'} data={rows} columns={columns} cardTitle={`Bobtail returns (${rows.length})`}
        settingsKey={`bobtail-returns-${mode}`} loading={loading} enablePagination globalPageSize={10}
        getRowId={row => String(row.leg_recno)} />
    </div>
  </section>;
}
